import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">InLeads</span>
            </Link>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Home
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Blog
              </Link>
              <Link to="/contatti" className="text-[#0047FF] font-semibold">
                Contatti
              </Link>
              <a href="https://app.inleads.it/#/accesso" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#0047FF] transition-colors">
                Accedi all'App
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Contattaci
          </h1>
          <p className="text-xl text-gray-600 mb-12">
            Siamo qui per aiutarti a crescere nel settore immobiliare
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Informazioni di Contatto
              </h2>
              
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0047FF] to-[#07396B] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                    <a 
                      href="mailto:info@inleads.it" 
                      className="text-[#0047FF] hover:underline text-lg"
                    >
                      info@inleads.it
                    </a>
                    <p className="text-gray-600 text-sm mt-1">
                      Risponderemo entro 24 ore lavorative
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Telefono</h3>
                    <a 
                      href="tel:+393513594112" 
                      className="text-[#0047FF] hover:underline text-lg"
                    >
                      +39 351 359 4112
                    </a>
                    <p className="text-gray-600 text-sm mt-1">
                      Lunedì - Venerdì, 9:00 - 18:00
                    </p>
                  </div>
                </div>

                {/* Office Hours */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Orari di Ufficio</h3>
                    <div className="text-gray-700">
                      <p>Lunedì - Venerdì: 9:00 - 18:00</p>
                      <p>Sabato: 9:00 - 13:00</p>
                      <p className="text-sm text-gray-600 mt-1">Domenica: Chiuso</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-[#0047FF] to-[#07396B] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">
                Inizia Subito con InLeads
              </h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Registrazione gratuita</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Setup in meno di 5 minuti</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Supporto dedicato</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  <span>Risultati garantiti</span>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href="https://app.inleads.it/#/accesso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white text-[#0047FF] py-3 px-6 rounded-lg font-semibold text-center hover:bg-gray-50 transition-colors"
                >
                  Accedi all'App InLeads
                </a>
                
                <a
                  href="https://app.inleads.it/#/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full border-2 border-white text-white py-3 px-6 rounded-lg font-semibold text-center hover:bg-white hover:text-[#0047FF] transition-colors"
                >
                  Richiedi Demo
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Domande Frequenti
            </h2>
            <p className="text-gray-600">
              Le risposte alle domande più comuni su InLeads
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Come posso iniziare?
              </h3>
              <p className="text-gray-600">
                Registrati gratuitamente sull'app InLeads. Il processo richiede meno di 5 minuti e puoi iniziare subito a utilizzare la piattaforma.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                InLeads è gratuito?
              </h3>
              <p className="text-gray-600">
                Sì, InLeads offre un accesso base gratuito con funzionalità premium disponibili per chi vuole massimizzare i risultati.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Quanto tempo per vedere risultati?
              </h3>
              <p className="text-gray-600">
                La maggior parte degli utenti vede i primi risultati entro 48 ore dalla registrazione, con match e opportunità concrete.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Supporto disponibile?
              </h3>
              <p className="text-gray-600">
                Offriamo supporto via email, telefono e chat nell'app. Il nostro team è specializzato nel settore immobiliare.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo-originale.png" 
                alt="InLeads Logo" 
                className="w-8 h-8 object-contain brightness-0 invert"
              />
              <span className="text-2xl font-bold">InLeads</span>
            </div>
            <p className="text-gray-400 mb-6">
              La piattaforma che connette agenzie e talenti nel settore immobiliare
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">
              Home
            </Link>
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
              Blog
            </Link>
            <Link to="/contatti" className="text-gray-400 hover:text-white transition-colors">
              Contatti
            </Link>
            <Link to="/formazione" className="text-gray-400 hover:text-white transition-colors">
              Formazione
            </Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
          </div>

          <div className="text-center mt-8 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-500">
              © 2025 InLeads. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;